import type { Validate } from '@ztechapps/common.hook'
import { useError, useMutation, useUserTimeZone } from '@ztechapps/common.hook'
import { PaymentMethodEnum } from '@ztechapps/common.contract'
import { toDate } from '@ztechapps/common.utils'
import isEmpty from 'lodash/isEmpty'
import merge from 'lodash/merge'
import { useCheckoutContext } from '../index'
import { orderFulfillPaymentQuery } from '../graphql'
import type { PaymentOptions } from '../types'
import { usePaymentValidation } from './usePaymentValidation'

export const useStartCheckPayment = (validate?: Validate<PaymentOptions>) => {
  const checkoutContext = useCheckoutContext()
  const fromCheckout = checkoutContext.fromCheckout
  const { document } = checkoutContext.orderDocumentResult
  const orderNetAmount = checkoutContext.orderNetAmount
  const { hasError } = checkoutContext.checkoutErrors
  const [, setShowLoadingOverlay] = checkoutContext.showLoadingOverlay
  const { onError, error } = useError()
  const [paymentOptions] = checkoutContext.paymentOptions
  const timeZone = useUserTimeZone()

  const [fulfill, ,] = useMutation({ query: orderFulfillPaymentQuery })
  const partialPaymentValidation = usePaymentValidation()

  const internalValidate: Validate<PaymentOptions> = (args?: PaymentOptions) => {
    const validateErrors = validate?.(args) ?? {}
    const paymentValidationErrors = partialPaymentValidation(args)

    // Check number is mandatory so the recorded payment is traceable to a specific check.
    // Trim first so a whitespace-only value does not satisfy the required rule.
    const checkErrors: Record<string, string> = {}
    if (isEmpty(args?.checkNumber?.trim())) {
      checkErrors.checkNumber = 'Required'
    }

    return merge({}, paymentValidationErrors, checkErrors, validateErrors)
  }

  const onStartPayment = async (args?: PaymentOptions) => {
    if (hasError()) return

    try {
      const paymentRemarks = args?.remarks ?? paymentOptions.remarks
      const paymentAmount = args?.amount ?? paymentOptions.amount
      const checkNumber = args?.checkNumber ?? paymentOptions.checkNumber

      // A check date is a calendar date in the user's local timezone, not an instant.
      // Anchor it to the user-tz start of day (defaulting to today) and store that instant
      // so it reads back as the same calendar day for the user regardless of the server's
      // or viewer's timezone offset — same convention as the checkout ShipmentDate field.
      const rawCheckDate = args?.checkDate ?? paymentOptions.checkDate
      const checkDate = (
        isEmpty(rawCheckDate) ? toDate(undefined, timeZone) : toDate(rawCheckDate, timeZone, { keepLocalTime: true })
      )
        .startOf('day')
        .toISOString()

      setShowLoadingOverlay(true)

      const orderId = document?._id

      await fulfill({
        input: {
          _id: orderId,
          orderId,
          fulfilledVia: 'Check',
          paymentMethod: PaymentMethodEnum.check,
          remarks: paymentRemarks,
          checkNumber,
          checkDate,
          paymentNet: paymentAmount ?? orderNetAmount,
          paymentGross: paymentAmount ?? orderNetAmount,
          fromCheckout,
        },
      })

      await checkoutContext?.onSuccess?.()
    } catch (e) {
      onError(e)
      throw e
    } finally {
      setShowLoadingOverlay(false)
    }
  }

  return {
    onStartPayment,
    hasError: !isEmpty(error),
    error,
    validate: internalValidate,
  }
}
